import { Injectable } from '@angular/core';

import { AnalyticsService } from '@app/core/analytics.service';
import {
  FLOW_ACCOUNT_UPDATE,
  LOGIN_AND_SECURITY_PAGE,
  MP_EVENT_LWA_EDIT_INFO_PAGE_VIEWED,
  MP_EVENT_LWA_EDIT_INFO_UPDATED,
  MP_EVENT_LWA_FAILURE_MODAL_SHOWN,
  MP_EVENT_LWA_LINK_ACCOUNT_ERROR,
  MP_EVENT_LWA_REDIRECT_TO_AMAZON_OAUTH,
  MP_EVENT_LWA_RETURN_FROM_AMAZON_OAUTH,
  MP_EVENT_LWA_SUCCESS_MODAL_SHOWN,
  MP_EVENT_PAGE_VIEWED,
  MP_EVENT_VERIFY_PASSWORD_ERROR,
  SWITCH_TO_LWA_MP_FLOW_VERSION,
  VERIFY_YOUR_PASSWORD,
  MP_LWA_DATA_SHARE_REVIEW_PAGE,
  MP_LWA_SHARE_AND_CONTINUE_CLICKED,
  MP_LWA_EMAIL_CLICKED,
  MP_LWA_DATA_SHARING_ERROR_VIEWED,
  MP_LWA_LINKING_CONFIRMATION_PAGE,
  MP_EVENT_LWA_TOKEN_GENERATION_ERROR,
  MP_EVENT_LWA_AUTH0_CALLBACK_ERROR,
} from '@app/core/mixpanel.constants';

@Injectable({
  providedIn: 'root',
})
export class LoginWithAmazonAnalyticsService extends AnalyticsService {
  readonly flow = FLOW_ACCOUNT_UPDATE;
  readonly flowVersion = SWITCH_TO_LWA_MP_FLOW_VERSION;

  trackVerifyIdentityPageViewed() {
    this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: VERIFY_YOUR_PASSWORD,
      source: LOGIN_AND_SECURITY_PAGE,
    });
  }

  trackVerifyPasswordError() {
    this.trackWithDefaultProperties(MP_EVENT_VERIFY_PASSWORD_ERROR, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: VERIFY_YOUR_PASSWORD,
    });
  }

  trackDataSharingPageViewed() {
    this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_DATA_SHARE_REVIEW_PAGE,
      source: VERIFY_YOUR_PASSWORD,
    });
  }

  // [LWA Switch] User clicked 'Share and Continue' on the demographics review page
  trackShareAndContinueClicked() {
    this.trackWithDefaultProperties(MP_LWA_SHARE_AND_CONTINUE_CLICKED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_DATA_SHARE_REVIEW_PAGE,
    });
  }

  trackEmailClicked() {
    this.trackWithDefaultProperties(MP_LWA_EMAIL_CLICKED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_DATA_SHARE_REVIEW_PAGE,
    });
  }

  trackDataSharingErrorViewed(errorDescription?: string) {
    this.trackWithDefaultProperties(MP_LWA_DATA_SHARING_ERROR_VIEWED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_DATA_SHARE_REVIEW_PAGE,
      error_description: errorDescription,
    });
  }

  // [LWA Switch] Browser is about to redirect to Amazon OAuth for account linking
  trackRedirectToAmazonOAuth() {
    this.trackWithDefaultProperties(MP_EVENT_LWA_REDIRECT_TO_AMAZON_OAUTH, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_DATA_SHARE_REVIEW_PAGE,
    });
  }

  // [LWA Switch] User returned from Amazon OAuth, about to call linkAmazonAccount mutation
  trackReturnFromAmazonOAuth() {
    this.trackWithDefaultProperties(MP_EVENT_LWA_RETURN_FROM_AMAZON_OAUTH, {
      flow: this.flow,
      flow_version: this.flowVersion,
    });
  }

  // [LWA Switch] Account linking succeeded — success confirmation modal shown to user
  trackSuccessModalShown() {
    this.trackWithDefaultProperties(MP_EVENT_LWA_SUCCESS_MODAL_SHOWN, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_LINKING_CONFIRMATION_PAGE,
    });
  }

  trackLinkingSuccessViewed() {
    this.trackWithDefaultProperties(MP_EVENT_PAGE_VIEWED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_LINKING_CONFIRMATION_PAGE,
    });
  }

  // [LWA Switch] Account linking failed — failure modal shown to user
  trackFailureModalShown() {
    this.trackWithDefaultProperties(MP_EVENT_LWA_FAILURE_MODAL_SHOWN, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_LINKING_CONFIRMATION_PAGE,
    });
  }

  // [LWA Switch] GraphQL/network error during linkAmazonAccount mutation
  trackLinkAccountError(errorMessage?: string) {
    this.trackWithDefaultProperties(MP_EVENT_LWA_LINK_ACCOUNT_ERROR, {
      flow: this.flow,
      flow_version: this.flowVersion,
      error_description: errorMessage,
    });
  }

  // [LWA Switch] User viewing the edit demographics page
  trackEditInfoPageViewed() {
    this.trackWithDefaultProperties(MP_EVENT_LWA_EDIT_INFO_PAGE_VIEWED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_DATA_SHARE_REVIEW_PAGE,
    });
  }

  // [LWA Switch] User submitted updated demographics from edit info page
  trackEditInfoUpdated() {
    this.trackWithDefaultProperties(MP_EVENT_LWA_EDIT_INFO_UPDATED, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: MP_LWA_DATA_SHARE_REVIEW_PAGE,
    });
  }

  // [LWA Switch] Verification token generation failed on direct-link route
  trackTokenGenerationError() {
    this.trackWithDefaultProperties(MP_EVENT_LWA_TOKEN_GENERATION_ERROR, {
      flow: this.flow,
      flow_version: this.flowVersion,
      module: VERIFY_YOUR_PASSWORD,
    });
  }

  // [LWA Switch/Hornbill] Auth0 returned an error code during OAuth callback
  trackAuth0CallbackError(errorCode: string) {
    this.trackWithDefaultProperties(MP_EVENT_LWA_AUTH0_CALLBACK_ERROR, {
      flow: this.flow,
      flow_version: this.flowVersion,
      error_description: errorCode,
    });
  }
}
